import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  FolderKanban, 
  Plus, 
  Tag,
  Users,
  Clock,
  FileText,
  MessageSquareQuote,
  Pencil,
  Trash2,
  Share2,
  UsersRound,
  MoreVertical,
  Star,
  ScrollText
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QuickActions, { projectActions } from "@/components/ui/QuickActions";

const STATUS_COLORS = {
  pending: "bg-yellow-500/20 text-yellow-400",
  active: "bg-green-500/20 text-green-400",
  paused: "bg-orange-500/20 text-orange-400",
  completed: "bg-blue-500/20 text-blue-400"
};

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharingProject, setSharingProject] = useState(null);
  const [shareData, setShareData] = useState({ team_id: "", permissions: "view" });
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    client_id: "",
    title: "",
    status: "active",
    tags: ""
  });

  const fetchData = async () => {
    try {
      const [projectsRes, clientsRes, teamsRes] = await Promise.all([
        api.get("/projects"),
        api.get("/clients"),
        api.get("/teams")
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setTeams(teamsRes.data.teams || []);
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({ client_id: "", title: "", status: "active", tags: "" });
    setEditingProject(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleOpenEdit = (project) => {
    setEditingProject(project);
    setFormData({
      client_id: project.client_id || "",
      title: project.title || "",
      status: project.status || "active",
      tags: Array.isArray(project.tags) ? project.tags.join(", ") : ""
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.title) {
      toast.error("Please select a client and enter a project title");
      return;
    }

    const payload = {
      ...formData,
      tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
    };

    try {
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, payload);
        toast.success("Project updated successfully!");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created successfully!");
      }
      handleCloseForm();
      fetchData();
    } catch (e) {
      console.error("Error saving project:", e);
      toast.error(editingProject ? "Failed to update project" : "Failed to create project");
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project? This will also remove associated proposals and testimonials.")) {
      return;
    }

    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Project deleted");
      fetchData();
    } catch (e) {
      console.error("Error deleting project:", e);
      toast.error("Failed to delete project");
    }
  };

  const handleOpenShare = (project) => {
    setSharingProject(project);
    setShareData({ team_id: teams[0]?.id || "", permissions: "view" });
    setShowShareDialog(true);
  };

  const handleShare = async () => {
    if (!shareData.team_id) {
      toast.error("Please select a team");
      return;
    }
    
    try {
      await api.post(`/teams/${shareData.team_id}/share`, {
        resource_type: "project",
        resource_id: sharingProject.id,
        permissions: shareData.permissions
      });
      toast.success(`Shared "${sharingProject.title}" with team`);
      setShowShareDialog(false);
      setSharingProject(null);
    } catch (e) {
      console.error("Error sharing project:", e);
      toast.error(e.response?.data?.detail || "Failed to share project");
    }
  };

  const handleNewProposal = (project) => {
    navigate(`/proposals?project=${project.id}`);
  };

  const handleRequestTestimonial = (project) => {
    navigate(`/testimonials?project=${project.id}`);
  };

  const getQuickActions = (project) => projectActions({
    onShare: () => handleOpenShare(project),
    onNewProposal: () => handleNewProposal(project),
    onRequestTestimonial: () => handleRequestTestimonial(project),
    onEdit: () => handleOpenEdit(project),
    onDelete: () => handleDelete(project.id)
  });

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Projects</h1>
          <p className="text-slate-400">Track your ongoing work</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl transition-colors"
          data-testid="add-project-btn"
        >
          <Plus className="w-5 h-5" />
          New Project
        </button>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => !open && handleCloseForm()}>
        <DialogContent className="bg-slate-800 border-slate-700" aria-describedby="project-form-description">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingProject ? "Edit Project" : "Create New Project"}
            </DialogTitle>
            <p id="project-form-description" className="sr-only">
              {editingProject ? "Update project information" : "Fill in the form to create a new project"}
            </p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Client *</label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white" data-testid="project-client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id} className="text-slate-300">
                      {client.name} {client.company && `(${client.company})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {clients.length === 0 && (
                <p className="text-xs text-yellow-400 mt-1">You need to create a client first</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Project Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none"
                placeholder="e.g., Website Redesign"
                data-testid="project-title-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Status</label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="pending" className="text-slate-300">Pending</SelectItem>
                  <SelectItem value="active" className="text-slate-300">Active</SelectItem>
                  <SelectItem value="paused" className="text-slate-300">Paused</SelectItem>
                  <SelectItem value="completed" className="text-slate-300">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Tags (comma-separated)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-indigo-500 focus:outline-none"
                placeholder="e.g., web, design, retainer"
                data-testid="project-tags-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleCloseForm}
                className="flex-1 px-4 py-2 text-slate-400 hover:text-slate-200 border border-slate-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={clients.length === 0}
                className="flex-1 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-600 text-white rounded-lg transition-colors"
                data-testid="submit-project-btn"
              >
                {editingProject ? "Save Changes" : "Create Project"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Projects Grid */}
      {projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <Card key={project.id} className="bg-slate-800/50 border-slate-700/50 hover:border-slate-600 transition-colors">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <FolderKanban className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{project.title}</h3>
                      <p className="text-sm text-slate-400 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {getClientName(project.client_id)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_COLORS[project.status]}>
                      {project.status}
                    </Badge>
                    <QuickActions 
                      actions={getQuickActions(project)}
                      triggerIcon={MoreVertical}
                      position="left"
                    />
                  </div>
                </div>

                {project.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {project.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 text-xs bg-slate-700/50 text-slate-400 px-2 py-1 rounded"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Module Actions */}
                <div className="flex gap-2 mb-3">
                  <Link
                    to={`/projects/${project.id}/proposals`}
                    className="flex-1 flex items-center justify-center gap-1 text-xs bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 py-2 px-2 rounded-lg transition-colors"
                    data-testid={`project-${project.id}-proposals`}
                  >
                    <FileText className="w-3 h-3" />
                    Proposals
                  </Link>
                  <Link
                    to={`/projects/${project.id}/contract`}
                    className="flex-1 flex items-center justify-center gap-1 text-xs bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 py-2 px-2 rounded-lg transition-colors"
                    data-testid={`project-${project.id}-contract`}
                  >
                    <ScrollText className="w-3 h-3" />
                    Contract
                  </Link>
                  <Link
                    to={`/projects/${project.id}/testimonials`}
                    className="flex-1 flex items-center justify-center gap-1 text-xs bg-green-500/10 hover:bg-green-500/20 text-green-400 py-2 px-2 rounded-lg transition-colors"
                    data-testid={`project-${project.id}-testimonials`}
                  >
                    <MessageSquareQuote className="w-3 h-3" />
                    Testimonials
                  </Link>
                </div>

                <div className="pt-3 border-t border-slate-700">
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Created {new Date(project.created_at).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardContent className="py-12 text-center">
            <FolderKanban className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-300 mb-2">No projects yet</h3>
            <p className="text-slate-500 mb-4">
              {clients.length === 0 
                ? "Create a client first, then add projects" 
                : "Create your first project to get started"}
            </p>
            <button
              disabled={clients.length === 0}
              onClick={handleOpenCreate}
              className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-600 text-white px-4 py-2 rounded-xl transition-colors"
              data-testid="add-project-empty-btn"
            >
              <Plus className="w-5 h-5" />
              New Project
            </button>
          </CardContent>
        </Card>
      )}

      {/* Share to Team Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" aria-describedby="share-project-dialog-description">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UsersRound className="w-5 h-5 text-green-400" />
              Share to Team
            </DialogTitle>
            <p id="share-project-dialog-description" className="sr-only">
              Share this project with your team members
            </p>
          </DialogHeader>
          
          {teams.length > 0 ? (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-400">
                Share <span className="text-white font-medium">{sharingProject?.title}</span> with:
              </p>
              
              <div>
                <label className="block text-sm text-slate-300 mb-1">Team</label>
                <Select value={shareData.team_id} onValueChange={(v) => setShareData({...shareData, team_id: v})}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {teams.map((team) => (
                      <SelectItem key={team.id} value={team.id} className="text-slate-300">
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm text-slate-300 mb-1">Permissions</label>
                <Select value={shareData.permissions} onValueChange={(v) => setShareData({...shareData, permissions: v})}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="view" className="text-slate-300">View only</SelectItem>
                    <SelectItem value="edit" className="text-slate-300">Can edit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowShareDialog(false)}
                  className="flex-1 px-4 py-2 text-slate-400 hover:text-slate-200 border border-slate-600 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShare}
                  className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="confirm-share-project-btn"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <UsersRound className="w-10 h-10 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No teams yet</p>
              <p className="text-slate-500 text-xs mt-1">Create a team first to share projects</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
